// ============================================================================
// UNIVERSAL ACTION HANDLERS - MISC ENTITY-SPECIFIC ACTIONS
// ============================================================================
// Contacts, tasks, companies, time entries, recurring charges, offerings,
// contracts, and users.

import { MODAL_IDS } from "@wtc/ui";
import type { UniversalActionHandlers } from "./types";
import { getFieldValue, getStatusFieldConfig } from "@/lib/utils/crm/status-field";
import { getEntityTableConfig, isEntityRegistryKey } from "@/lib/entity-registry";
import {
  SOCIAL_POST_PLATFORM_OPTIONS,
} from "@/features/social-media-posts/config/options";

/**
 * Entity-name / surface signals that mean "this row IS an app_user" (the admin
 * users table, the employees registry, or the user detail modal). On these
 * surfaces, access-grant handlers treat `item.id` as the user id and open the
 * USER-centric grant modals.
 */
const USER_SURFACE_KEYS = new Set(["admin_users", "employees", "user", "users"]);

function isUserSurface(context: {
  entityName?: string;
  surfaceEntityType?: string;
  pageConfig?: { entityName?: string };
}): boolean {
  const candidates = [
    context.surfaceEntityType,
    context.entityName,
    context.pageConfig?.entityName,
  ];
  return candidates.some((key) => !!key && USER_SURFACE_KEYS.has(key));
}

/**
 * Misc Entity-Specific Actions
 */
export const miscEntityActions: UniversalActionHandlers = {
  // Contact-specific actions
  changeStatus: async (item, context) => {
    // Resolve the status-field config from the entity REGISTRY (the single,
    // codegen-aligned source of SimpleTableConfig) keyed by entityName — not
    // context.pageConfig.config, whose type (DetailPageConfig) differs from the
    // SimpleTableConfig getStatusFieldConfig needs, so it mis-resolved on detail
    // pages and silently fell back to "status" with no options.
    const tableConfig = isEntityRegistryKey(context.entityName)
      ? getEntityTableConfig(context.entityName)
      : undefined;
    const statusFieldConfig = getStatusFieldConfig(tableConfig);
    const statusField = statusFieldConfig?.fieldName ?? "status";
    const currentStatus = getFieldValue(item, statusField);
    // Open a modal or drawer for status selection.
    // The modal renders globally (app/layout.tsx) outside this action provider,
    // so it cannot reach context.onUpdateItem. Hand it a bound updater that
    // mutates ONLY the status field (never the whole denormalized item).
    context.onOpenModal("change-status", {
      item,
      currentStatus,
      statusField,
      statusOptions: statusFieldConfig?.fieldConfig?.options,
      onConfirm: async (newStatus: string) => {
        await context.onUpdateItem({ [statusField]: newStatus });
      },
    });
  },

  // Social media post: publish NOW. The ONLY web path that actually fans the
  // post out to live platforms (via Zapier) — setting status by hand never
  // publishes. Opens the irreversible double-confirm modal, which POSTs to
  // /api/marketing/social/publish. See the SALES_CADENCE_SEQUENCES sibling docs
  // and MARKETING_BRANDS. The modal is global/context-free (data-payload).
  publishSocialPost: async (item, context) => {
    const platformKeys: string[] = Array.isArray(item.platforms) ? item.platforms : [];
    const platformLabels = platformKeys.map(
      (key) =>
        SOCIAL_POST_PLATFORM_OPTIONS.find((o) => o.value === key)?.label ?? key,
    );
    // The brand ID travels in the payload; PublishSocialPostModal resolves it to a
    // display name from the live brand list. It used to be looked up here against a
    // module constant, which stopped working when brands became database rows.
    const brandLabel = (item.brand as string | undefined) ?? null;
    const bodyExcerpt =
      typeof item.body === "string" && item.body.trim().length > 0
        ? `${item.body.trim().slice(0, 60)}${item.body.trim().length > 60 ? "…" : ""}`
        : null;

    context.onOpenModal(MODAL_IDS.socialMediaPosts.publishConfirm, {
      postId: item.id,
      postLabel: item.title || bodyExcerpt,
      brandLabel,
      platformLabels,
      onConfirmed: async () => {
        await context.onRefresh?.();
      },
    });
  },

  generateContract: async (item, context) => {
    // Navigate to contract generation page or open modal
    context.onNavigate(`/contracts/template/965d238e-4b59-44c6-91d2-5d3990d3abdd?contact_id=${item.id}`);
  },

  viewCompany: async (item, context) => {
    // Navigate to company detail page with better ID support
    if (item.company_id) {
      context.onNavigate(`/companies/${item.company_id}`);
    } else if (item.company_name) {
      // Fallback to search by company name if no company_id
      context.onNavigate(`/companies?search=${encodeURIComponent(item.company_name)}`);
    } else {
      // Different message based on entity type
      const entityType = item.ticket_number
        ? "ticket"
        : item.task_title
          ? "task"
          : item.hours
            ? "time entry"
            : item.activity_type
              ? "activity"
              : "item";
      context.onToast(`No company associated with this ${entityType}`, "warning");
    }
  },

  // Task-specific actions
  duplicateTask: async (item, context) => {
    context.onNavigate(`/tasks/create?duplicate=${item.id}`);
  },

  addComment: async (item, context) => {
    context.onOpenModal("add-comment", {
      item,
      entityId: item.id,
    });
  },

  openCallContactPicker: async (item, context) => {
    context.onOpenModal("call-contact-picker", {
      item,
      entityId: item.id,
      companyId: item.company_id ?? null,
      contactId: item.contact_id ?? null,
    });
  },

  addTaskNote: async (item, context) => {
    context.onOpenModal(MODAL_IDS.tasks.taskAddNote, {
      taskId: item.id,
      item,
    });
  },

  // validate-action-modal-mounts-allow: set-due-date verified mounted (SetDueDateModal in UniversalDetailPageModals + EntityPageModals)
  // validate-action-modal-mounts-allow: convert-offering-to-deal verified mounted (ConvertOfferingToDealModal in app/offerings/[id]/page.tsx)
  setDueDate: async (item, context) => {
    context.onOpenModal(MODAL_IDS.dueDate.set, {
      title: item.title ?? item.name ?? item.task_title ?? "this item",
      currentDueDate: item.due_date ?? null,
      onSave: async (value: string | null) => {
        await context.onUpdateItem({ due_date: value || null });
        context.onToast(value ? "Due date updated" : "Due date cleared", "success");
      },
    });
  },

  exportItem: async (_item, context) => {
    context.onToast("Export started", "info");
  },

  reportIssue: async (item, context) => {
    context.onOpenModal("report-issue", {
      item,
      entityId: item.id,
    });
  },

  duplicateContact: async (item, context) => {
    context.onNavigate(`/contacts/create?duplicate=${item.id}`);
  },

  duplicateTimeEntry: async (item, context) => {
    context.onNavigate(`/time-tracking/entries/create?duplicate=${item.id}`);
  },

  syncToQuickBooks: async (item, context) => {
    context.onToast(
      `Syncing time entry ${item.time_entry_id || item.id} to QuickBooks is not yet available.`,
      "info"
    );
  },

  // Grant EMPLOYEE access.
  // - On a USER surface (admin users / employees): an employee (the user row)
  //   is granted access to one or more CONTACTS. Opens the user-centric global
  //   modal with userId = the user's id.
  // - On a CONTACT surface: a single contact is shared with employees. Keeps
  //   the existing contact-centric flow.
  grantEmployeeAccess: async (item, context) => {
    if (isUserSurface(context)) {
      context.onOpenModal(MODAL_IDS.users.grantEmployeeAccess, {
        userId: item.id,
        user: item,
      });
      return;
    }
    context.onOpenModal(MODAL_IDS.employees.grantAccess, {
      contactId: item.contact_id || item.id,
      item,
    });
  },

  // Grant CLIENT access: a CLIENT user (the user row) is granted access to a
  // CONTACT at an access level (read_only | limited | full). User-centric only.
  grantClientAccess: async (item, context) => {
    context.onOpenModal(MODAL_IDS.users.grantClientAccess, {
      userId: item.id,
      user: item,
    });
  },

  // Grant GOOGLE DRIVE access: the current (Google-connected) admin lends their
  // Drive OAuth connection to this Drive-less admin (the user row). The modal
  // validates that the operator actually has a connection. See
  // GOOGLE_DRIVE_ACCESS_DELEGATION.
  grantDriveAccess: async (item, context) => {
    context.onOpenModal(MODAL_IDS.users.grantDriveAccess, {
      userId: item.id,
      user: item,
    });
  },

  consolidateContact: async (item, context) => {
    context.onOpenModal("consolidate-contact", {
      contactId: item.contact_id || item.id,
      item,
    });
  },

  consolidateCompany: async (item, context) => {
    context.onOpenModal("consolidate-company", {
      companyId: item.id,
      item,
    });
  },

  // Recurring Charge-specific actions
  chargeCardOnFile: async (item, context) => {
    // Open modal to charge card on file with optional amount customization
    context.onOpenModal("charge-card-on-file", {
      chargeId: item.id,
      item,
      amount: item.metadata?.amount,
      currency: item.metadata?.currency || "USD",
      customerName: item.metadata?.customerName || item.customer_id,
    });
  },

  viewDetails: async (item, context) => {
    // Navigate to detail page
    context.onNavigate(`/recurring-charges/${item.id}`);
  },

  changeSchedule: async (item, context) => {
    // Open modal to change schedule
    context.onOpenModal("change-schedule", {
      chargeId: item.id,
      item,
      currentSchedule: item.schedule_type,
    });
  },

  deleteRecurringCharge: async (item, context) => {
    // Confirm deletion before proceeding
    if (window.confirm("Are you sure you want to delete this recurring charge? This action cannot be undone.")) {
      await context.onDeleteItem();
    }
  },

  // Offering-specific actions
  viewOffering: async (item, context) => {
    if (item.id) {
      context.onNavigate(`/offerings/${item.id}`);
    } else {
      context.onToast("No offering ID available", "warning");
    }
  },

  downloadOfferingPdf: async (item, context) => {
    const { generateOfferingPDF } = await import("@/lib/utils/pdf/offering-pdf-export");
    generateOfferingPDF(item as import("@/lib/types/database/offerings").Offering);
    context.onToast("Offering PDF downloaded", "success");
  },

  convertToDeal: async (item, context) => {
    context.onOpenModal("convert-offering-to-deal", { offering: item });
  },

  // Contract-specific actions

  // Edit the Hub-side filing of a contract (company/contact/deal linkage,
  // document name, notes). Opens the global EditContractModal, which GET/PATCHes
  // /api/contracts/[id] through the same contract-store seam the webhook writes
  // through. Never touches the eSignatures provider — a sent contract is
  // immutable there (recipient identity / document body / lifecycle are
  // provider-owned).
  editContract: async (item, context) => {
    context.openModalTypeSafe(MODAL_IDS.contracts.edit, {
      contractId: String(item.id),
      onSaved: async () => {
        await context.onRefresh?.();
      },
    });
  },

  // Renew: start a fresh contract for the same client, carrying the source
  // contract forward. eSignatures has no "renew" API, so this launches the
  // normal creation workflow pre-filled from the source (client + best-effort
  // template) with a "Renewing …" banner. The new contract is sent through the
  // usual flow and recorded as its own row via the send path.
  renewContract: async (item, context) => {
    context.onNavigate(`/contracts?renewFrom=${encodeURIComponent(String(item.id))}`);
  },

  // Terminate is an alias for Withdraw: eSignatures can't un-sign a contract, so
  // "terminate" is the same provider withdraw + status flow (stops signing +
  // reminders on a pending contract, marks the row withdrawn). Opens the same
  // context-free confirm modal the Withdraw action uses.
  terminateContract: async (item, context) => {
    context.onOpenModal(MODAL_IDS.contracts.withdraw, {
      contractId: String(item.id),
      contractLabel:
        (item as { document_name?: string | null }).document_name ??
        (item as { template_name?: string | null }).template_name ??
        null,
      onConfirmed: async () => {
        await context.onRefresh?.();
      },
    });
  },

  // Duplicate: launch the creation workflow pre-filled from the source contract
  // (client + best-effort template). Routes into the real /contracts flow (there
  // is no /contracts/create route); the page resolves the source via
  // GET /api/contracts/[id].
  duplicateContract: async (item, context) => {
    context.onNavigate(`/contracts?duplicateFrom=${encodeURIComponent(String(item.id))}`);
  },

  // Resend the e-signature request to every signer. Direct action: POSTs to the
  // employee-authed resend route (which resolves signer ids from eSignatures),
  // toasts the result, then refreshes the view.
  resendContract: async (item, context) => {
    try {
      const response = await fetch(
        `/api/contracts/${encodeURIComponent(String(item.id))}/resend`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const payload: { success?: boolean; resent?: number; error?: string } = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !payload.success) {
        context.onToast(payload.error || "Failed to resend signature request", "error");
        return;
      }

      const count = payload.resent ?? 0;
      context.onToast(
        count === 1
          ? "Signature request resent to 1 signer"
          : `Signature request resent to ${count} signers`,
        "success",
      );
      await context.onRefresh?.();
    } catch {
      context.onToast("Failed to resend signature request", "error");
    }
  },

  // Withdraw / void the contract (DESTRUCTIVE, admin). Opens the global confirm
  // modal; the modal POSTs to the admin withdraw route on confirm. Pass an
  // onConfirmed callback so the source view refreshes after a successful void.
  // There is no eSignatures "reset" API — the real reset is Withdraw, then
  // re-send via the custom send flow (the modal copy explains this).
  withdrawContract: async (item, context) => {
    context.onOpenModal(MODAL_IDS.contracts.withdraw, {
      contractId: String(item.id),
      contractLabel:
        (item as { document_name?: string | null }).document_name ??
        (item as { template_name?: string | null }).template_name ??
        null,
      onConfirmed: async () => {
        await context.onRefresh?.();
      },
    });
  },

  // Export: open the executed PDF in a new tab for download.
  //
  // Resolved through GET /api/contracts/[id]/document, which mints a fresh
  // presigned URL against our durable S3 archive. It deliberately does NOT use
  // `contracts.signed_pdf_url` — that's the eSignatures link, which expires
  // ~3 days after the signing event, so exporting through it silently failed on
  // every contract older than that.
  exportContract: async (item, context) => {
    try {
      const response = await fetch(
        `/api/contracts/${encodeURIComponent(String(item.id))}/document?variant=signed&disposition=attachment`,
      );
      const payload: {
        status?: string;
        url?: string;
        reason?: string;
        error?: string;
      } = await response.json().catch(() => ({}));

      if (!response.ok) {
        context.onToast(payload.error || "Couldn't export the contract.", "error");
        return;
      }
      if (payload.status !== "ready" || !payload.url) {
        context.onToast(
          payload.reason ||
            "This contract isn't signed yet — there's no executed PDF to export.",
          "warning",
        );
        return;
      }
      if (typeof window !== "undefined") {
        window.open(payload.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      context.onToast("Couldn't export the contract. Try again.", "error");
    }
  },

  // User-specific actions
  resetPassword: async (item, context) => {
    context.onOpenModal("reset-password", { userId: item.id, item });
  },

  toggleActiveStatus: async (item, context) => {
    context.onOpenModal("toggle-active-status", { userId: item.id, item });
  },

  manageRoles: async (item, context) => {
    context.onOpenModal("manage-roles", { userId: item.id, item });
  },
};
